import { z } from "zod";

/**
 * SÖZLEŞME EKLERİ (CLOS-121 `[103]`).
 *
 * ### ⚠ Ek, ana belgenin YERİNE GEÇMEZ
 *
 * Kart: *"ek dosya ana sözleşme belgesi, sözleşme versiyonu veya imzalı belge olarak
 * işaretlenmez. Ekin değiştirilmesi ana sözleşmenin YENİ VERSİYONUNU ÜRETMEZ."* Ekler
 * bağımsız ve opsiyonel kayıtlar; versiyon geçmişine hiç dokunmuyorlar.
 *
 * ### ⚠ Sınırlar KONFİGÜRASYONDAN
 *
 * Kartın AÇIK KARARI: *"ek dosyalarda izin verilen biçim, boyut ve adet sınırları
 * KONFİGÜRE EDİLECEKTİR. Parametreler hard-code edilmez."* Değerler `config/
 * contractAttachment.json`'da; koda gömülü bir sayı, kimsenin sorgulamadığı bir gerçeğe
 * dönüşürdü.
 */

export const contractAttachmentPolicySchema = z.object({
  /** İzin verilen içerik türleri — ana belgeden DAHA GENİŞ (ek bir tablo, resim olabilir). */
  allowedContentTypes: z.array(z.string().min(1)).min(1),
  maxBytes: z.number().int().positive(),
  /** Bir sözleşmeye eklenebilecek azami AKTİF ek sayısı. */
  maxAttachmentsPerContract: z.number().int().positive(),
});
export type ContractAttachmentPolicy = z.infer<typeof contractAttachmentPolicySchema>;

/**
 * Reddetme sebepleri — kapalı küme.
 *
 * ⚠ `[070]`'in kuralı burada da geçerli: sebep kullanıcıya DÜZELTİLEBİLİR bir şey
 * söylüyor, doğrulayıcının iç mantığını anlatmıyor.
 */
export const CONTRACT_ATTACHMENT_REJECTIONS = [
  "unsupported_type",
  "too_large",
  "empty",
  "type_mismatch",
  "not_scanned",
  "limit_reached",
  "already_attached",
] as const;
export type ContractAttachmentRejection = (typeof CONTRACT_ATTACHMENT_REJECTIONS)[number];

export const CONTRACT_ATTACHMENT_REJECTION_NAMES: Record<ContractAttachmentRejection, string> = {
  unsupported_type: "Bu dosya türü ek olarak yüklenemiyor.",
  too_large: "Dosya boyutu ek sınırını aşıyor.",
  empty: "Dosya boş.",
  type_mismatch: "Dosyanın gerçek türü beyan edilen türle uyuşmuyor.",
  not_scanned: "Dosyanın güvenlik taraması tamamlanmadı.",
  limit_reached: "Bu sözleşme için ek sayısı sınırına ulaşıldı.",
  already_attached: "Bu dosya zaten bu sözleşmeye eklenmiş.",
};

export const createContractAttachmentSchema = z.object({
  storageObjectId: z.string().uuid("Geçerli bir dosya seçin"),
  /**
   * Kullanıcıya gösterilen başlık. Dosya adından AYRI: "imza_sirkuleri_son_v3.pdf" bir
   * dosya adıdır, "İmza sirküleri" ise ekin ne olduğudur.
   */
  title: z.string().trim().min(1, "Başlık zorunludur").max(160, "Başlık en çok 160 karakter"),
});
export type CreateContractAttachmentInput = z.infer<typeof createContractAttachmentSchema>;

export const contractAttachmentListQuerySchema = z.object({
  /** Kaldırılmış ekler VARSAYILAN OLARAK gizli; geçmiş kaydı duruyor ama liste güncel. */
  includeRemoved: z
    .union([z.boolean(), z.enum(["true", "false"])])
    .transform((v) => v === true || v === "true")
    .default(false),
});
export type ContractAttachmentListQuery = z.infer<typeof contractAttachmentListQuerySchema>;

/**
 * Listede ve detayda gösterilen ek.
 *
 * ⚠ Depolama adresi YOK (`[090]`/`[101]` kuralı): indirme ayrı ve süreli bir sözleşmenin
 * işi, erişim kararı o an yeniden veriliyor.
 */
export type ContractAttachmentView = {
  id: string;
  title: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  /** Yükleme anındaki görünen ad; hesap kapansa da korunuyor. */
  uploadedByName: string;
  createdAt: string;
  removedAt: string | null;
  removedByName: string | null;
};
